import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { useSaveBdArtifact } from '../../../hooks/useBasicDesign.js';
import { ApiError } from '../../../lib/api/_request.js';
import { sectionStatus } from './bd-defs.js';
import { BdFormatHint } from './BdFormatHint.js';
import { BdHistory } from './BdHistory.js';
import { BdNotApplicableForm } from './BdNotApplicableForm.js';
import { BdRowsEditor } from './BdRowsEditor.js';
import { BdRowsTable } from './BdRowsTable.js';
import { BdRowsView } from './BdRowsView.js';
import { BdSection } from './BdSection.js';
import { ImpactListView } from './ImpactListView.js';
import { RiskListView } from './RiskListView.js';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import type {
  BdArtifact,
  BdArtifactDef,
  BdRow,
  BdSavedArtifact,
} from '../../../lib/api/basic-design.js';
import type { BdRowsLayout } from '../../../lib/bd-view-settings.js';

export interface BdArtifactPanelProps {
  /** 基本設計のチケット */
  bdKey: string;
  /** 開いている節の定義 */
  def: BdArtifactDef;
  /** 保存済みの節（参照の手がかりにも使う） */
  saved: Partial<Record<BdArtifact, BdSavedArtifact>>;
  /** 行の見せ方（個人の見え方）。省略時はカード */
  layout?: BdRowsLayout;
  /** 隠した列（個人の見え方） */
  hiddenColumns?: readonly string[];
  /** 行の見せ方を変える。渡さなければ切り替えを出さない */
  onLayoutChange?: (layout: BdRowsLayout) => void;
  /** 列の表示を切り替える。渡さなければ切り替えを出さない */
  onToggleColumn?: (column: string) => void;
}

/** 絞り込みのある専用の見た目を持つ節。表・カードの切り替えと列の表示は出さない。 @graph-connects none */
const DEDICATED_VIEWS = new Set(['impact_list', 'risk_list']);

/** 読む形の指定。 @graph-connects none */
interface RowsViewProps {
  def: BdArtifactDef;
  rows: BdRow[];
  layout: BdRowsLayout;
  hiddenColumns: readonly string[];
}

/**
 * 保存済みの行を読む形。影響一覧とリスク一覧は絞り込みのある専用の見た目、ほかは定義から描き、
 * 見ている人の選んだ表かカードで、隠した列を除いて出す。
 * @graph-connects ImpactListView [calls] 影響一覧を読む
 * @graph-connects RiskListView [calls] リスク一覧を読む
 * @graph-connects BdRowsView [calls] 節の行をカードで読む
 * @graph-connects BdRowsTable [calls] 節の行を表で読む
 */
function RowsView({ def, rows, layout, hiddenColumns }: RowsViewProps) {
  if (def.key === 'impact_list') return <ImpactListView def={def} rows={rows} />;
  if (def.key === 'risk_list') return <RiskListView def={def} rows={rows} />;
  if (layout === 'table') {
    return (
      <BdRowsTable
        def={def}
        rows={rows}
        columns={def.columns.filter((c) => !hiddenColumns.includes(c.key))}
      />
    );
  }
  return <BdRowsView def={def} rows={rows} hiddenColumns={hiddenColumns} />;
}

interface ViewOptionsProps {
  def: BdArtifactDef;
  layout: BdRowsLayout;
  hiddenColumns: readonly string[];
  onLayoutChange: (layout: BdRowsLayout) => void;
  onToggleColumn: (column: string) => void;
}

/**
 * 行の見せ方（カード・表）と出す列の切り替え。見ている人だけの見え方で、保存した中身は変えない。
 * @graph-connects none
 */
function ViewOptions({
  def,
  layout,
  hiddenColumns,
  onLayoutChange,
  onToggleColumn,
}: ViewOptionsProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-start gap-3 text-xs">
      <TabGroup ariaLabel={t('bd.view.layoutAria', { title: def.title })}>
        {(['cards', 'table'] as const).map((l) => (
          <TabButton
            key={l}
            active={layout === l}
            label={t(`bd.view.layout.${l}`)}
            onClick={() => {
              onLayoutChange(l);
            }}
          />
        ))}
      </TabGroup>
      <details className="rounded-lg border border-[var(--color-border)] px-3 py-1.5">
        <summary className="cursor-pointer text-[var(--color-text-secondary)]">
          {t('bd.view.columns', {
            shown:
              def.columns.length - def.columns.filter((c) => hiddenColumns.includes(c.key)).length,
            total: def.columns.length,
          })}
        </summary>
        <fieldset className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
          <legend className="sr-only">{t('bd.view.columnsAria', { title: def.title })}</legend>
          {def.columns.map((column) => (
            <label
              key={column.key}
              className="flex items-center gap-2 text-[var(--color-text-primary)]"
            >
              <Checkbox
                checked={!hiddenColumns.includes(column.key)}
                onChange={() => {
                  onToggleColumn(column.key);
                }}
              />
              {column.label}
            </label>
          ))}
        </fieldset>
      </details>
    </div>
  );
}

/**
 * 基本設計の節 1 つ。読む・編集する・該当なしで保存する・履歴を見る。
 *
 * 編集中の行・保存の注意はこの節にだけ属する。呼び出し側は節を切り替えるときにキーを
 * 変えてこの部品を作り直す（下書きが別の節に持ち越されて、そちらに保存されないように）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節 1 つを読み、定義から組み立てたフォームで編集して保存し、任意の節は該当なしで保存し、保存の履歴を見る
 * @graph-connects useSaveBdArtifact [calls] 節の保存
 * @graph-connects BdSection [calls] 節の見出しと空の案内
 * @graph-connects BdRowsEditor [calls] 行の編集
 * @graph-connects BdFormatHint [calls] 書き方
 * @graph-connects BdHistory [calls] 保存の履歴
 * @graph-connects BdNotApplicableForm [calls] 該当なしの理由の入力
 * @graph-connects ViewOptions [calls] 行の見せ方と列の切り替え
 */
export function BdArtifactPanel({
  bdKey,
  def,
  saved,
  layout = 'cards',
  hiddenColumns = [],
  onLayoutChange,
  onToggleColumn,
}: BdArtifactPanelProps) {
  const [editing, setEditing] = useState(false);
  const [askingReason, setAskingReason] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const save = useSaveBdArtifact(bdKey);
  const current = saved[def.key];
  const status = sectionStatus(def, current);
  const hasRows = current !== undefined && current.rows.length > 0;

  const submit = (rows: BdRow[], notApplicableReason?: string) => {
    save.mutate(
      { artifact: def.key, rows, notApplicableReason },
      {
        onSuccess: (result) => {
          setWarnings(result.warnings);
          setEditing(false);
          setAskingReason(false);
        },
      },
    );
  };
  // 編集で行をすべて消して保存すると 0 行（該当なし）になるので、送る前に理由を聞く
  const saveEdited = (rows: BdRow[]) => {
    if (rows.length === 0 && def.requirement === 'optional') {
      save.reset();
      setAskingReason(true);
      return;
    }
    submit(rows);
  };
  // 行のある節を「該当なし」にすると行が消えるので、行が無いときにだけ出す。押したらまず理由を聞く
  const notApplicable =
    def.requirement === 'optional' && !hasRows
      ? () => {
          save.reset();
          setAskingReason(true);
        }
      : undefined;

  return (
    <div className="space-y-3">
      <BdSection
        def={def}
        status={status}
        count={current === undefined ? null : current.rows.length}
        notApplicableReason={current?.notApplicableReason ?? ''}
        editing={editing}
        warnings={warnings}
        markingNotApplicable={!editing && save.isPending}
        onEdit={() => {
          setWarnings([]);
          save.reset();
          setEditing(true);
        }}
        onNotApplicable={notApplicable}
      >
        {editing ? (
          <div className="space-y-3">
            <BdFormatHint artifact={def.key} label={def.title} />
            <BdRowsEditor
              def={def}
              saved={saved}
              savedRows={current?.rows ?? null}
              saving={save.isPending}
              error={save.error}
              warnings={warnings}
              onSave={saveEdited}
              onCancel={() => {
                save.reset();
                setEditing(false);
              }}
              onNotApplicable={notApplicable}
            />
          </div>
        ) : current === undefined ? null : (
          <div className="space-y-3">
            {onLayoutChange === undefined ||
            onToggleColumn === undefined ||
            DEDICATED_VIEWS.has(def.key) ? null : (
              <ViewOptions
                def={def}
                layout={layout}
                hiddenColumns={hiddenColumns}
                onLayoutChange={onLayoutChange}
                onToggleColumn={onToggleColumn}
              />
            )}
            <RowsView def={def} rows={current.rows} layout={layout} hiddenColumns={hiddenColumns} />
          </div>
        )}
      </BdSection>
      {askingReason ? (
        <BdNotApplicableForm
          title={def.title}
          saving={save.isPending}
          onSubmit={(reason) => {
            submit([], reason);
          }}
          onCancel={() => {
            save.reset();
            setAskingReason(false);
          }}
        />
      ) : null}
      {!editing && save.error !== null ? <BdSaveErrorNote error={save.error} /> : null}
      {current === undefined ? null : <BdHistory bdKey={bdKey} def={def} />}
    </div>
  );
}

/**
 * 編集を開かずに保存した（該当なし）ときの失敗。フォームが無いので、理由をここに出す。
 * @graph-connects ApiError [references] 権限の無い保存の判別
 */
function BdSaveErrorNote({ error }: { error: Error }) {
  const { t } = useTranslation();
  return (
    <p
      role="alert"
      className="rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error-muted)] px-4 py-3 text-sm text-[var(--color-error-text)]"
    >
      {error instanceof ApiError && error.status === 403 ? t('bd.save.forbidden') : error.message}
    </p>
  );
}
